/**
 * Internationalization Context
 * Provides language switching between Chinese (zh-TW) and English
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation as useI18nextTranslation } from 'react-i18next';
import zhTW from '../i18n/zh-TW.json';
import en from '../i18n/en.json';

export type Language = 'zh-TW' | 'en';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  getLocalizedValue: <T extends { zh: string; en?: string | null }>(value: T | null | undefined) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const STORAGE_KEY = 'blia-events-language';

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources: {
      'zh-TW': { translation: zhTW },
      en: { translation: en },
    },
    lng: localStorage.getItem(STORAGE_KEY) || 'zh-TW',
    fallbackLng: 'zh-TW',
    interpolation: {
      escapeValue: false,
    },
  });

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [language, setLanguageState] = useState<Language>(
    () => (localStorage.getItem(STORAGE_KEY) as Language) || 'zh-TW'
  );

  const { t: i18nT } = useI18nextTranslation();

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang === 'zh-TW' ? 'zh-Hant-TW' : 'en';
    document.documentElement.setAttribute('translate', 'no');
  }, []);

  useEffect(() => {
    // Set initial document language and prevent browser auto-translation
    document.documentElement.lang = language === 'zh-TW' ? 'zh-Hant-TW' : 'en';
    document.documentElement.setAttribute('translate', 'no');
    document.documentElement.classList.add('notranslate');
  }, [language]);

  const t = useCallback((key: string, options?: Record<string, unknown>): string => {
    return i18nT(key, options as Record<string, string>);
  }, [i18nT]);

  const getLocalizedValue = useCallback(<T extends { zh: string; en?: string | null }>(
    value: T | null | undefined
  ): string => {
    if (!value) return '';
    if (language === 'en' && value.en) {
      return value.en;
    }
    return value.zh;
  }, [language]);

  const value: I18nContextType = {
    language,
    setLanguage,
    t,
    getLocalizedValue,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

// Re-export useTranslation for components that need direct access
export { useI18nextTranslation as useTranslation };

export { I18nContext };
